import { FileText, Receipt } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/formatters";
import { cn } from "@/lib/utils";

const SECTION_TITLE_CLASS = "flex items-center gap-3.5 text-[19px] font-bold leading-tight tracking-normal";
const SECTION_TITLE_ICON_CLASS = "h-5 w-5 shrink-0 text-muted-foreground";

export interface OrderInvoice {
  invoiceNumber: string;
  /** Opcional: o faturamento comercial só recebe o número da NF. */
  status?: string | null;
  statusDate?: string | null;
}

interface OrderInvoicesProps {
  invoices: OrderInvoice[];
  isLoading?: boolean;
  className?: string;
}

export function OrderInvoices({ invoices, isLoading = false, className }: OrderInvoicesProps) {
  return (
    <Card className={cn("border-border dark:border-border shadow-lg", className)}>
      <CardHeader>
        <CardTitle className={SECTION_TITLE_CLASS}>
          <Receipt className={SECTION_TITLE_ICON_CLASS} aria-hidden="true" />
          Notas Fiscais
        </CardTitle>
        <CardDescription>Consulte as notas fiscais geradas para esse pedido.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4" data-testid="order-invoices-loading">
            {[1, 2].map((i) => (
              <div key={i} className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-center border-b pb-4">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-6 w-32" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3].map((j) => (
                    <Skeleton key={j} className="h-20 w-full rounded-md" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : invoices.length > 0 ? (
          <div className="space-y-6">
            {invoices.map((invoice) => (
              <div key={invoice.invoiceNumber} className="border rounded-lg p-4 space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div>
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      NF: {Number(invoice.invoiceNumber)}
                    </h3>
                    {invoice.status && (
                      <p className="text-sm text-muted-foreground">
                        Status: <span className="font-medium text-foreground">{invoice.status}</span>
                      </p>
                    )}
                  </div>
                  {invoice.statusDate && (
                    <Badge variant="outline" className="bg-muted/30">
                      {formatDateTime(new Date(invoice.statusDate))}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center border-dashed border-2 rounded-lg">
            <Receipt className="h-10 w-10 text-muted-foreground opacity-50" />
            <p className="mt-2 font-medium">Nenhuma OV emitida</p>
            <p className="text-sm text-muted-foreground">As OV aparecerão aqui assim que forem geradas.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
